import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Crown, Check, X, Clock, CreditCard } from "@phosphor-icons/react";

/**
 * Post-Stripe-Checkout landing page.
 *
 * Handles BOTH integration paths:
 *  1) New recurring subscription (POST /api/pro/subscribe) → poll /api/pro/me
 *     until `subscription_status` flips to `trialing` or `active` (webhook
 *     fulfils a moment after Stripe redirects back here).
 *  2) Legacy one-shot package (POST /api/payments/checkout/session) → poll
 *     /api/payments/checkout/status/{id} until `payment_status === "paid"`.
 *
 * The user lands here either way with `?session_id=cs_…`. We try the
 * subscription path first; if /api/pro/me doesn't go Pro within a few
 * seconds, we fall back to checking the legacy endpoint.
 */
export default function ProSuccess() {
  const { lang } = useI18n();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const nav = useNavigate();
  const polling = useRef(false);

  // Composite UI state.
  //  status: pending | trialing | active | paid | expired | error
  //  proSummary: response from /api/pro/me (if any)
  const [state, setState] = useState({ status: "pending", proSummary: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId || polling.current) return;
    polling.current = true;

    let attempts = 0;
    const MAX = 10;
    let syncAttempted = false;

    const tick = async () => {
      attempts += 1;

      // Try the subscription path first — most new checkouts will be subs.
      try {
        const { data: pro } = await api.get("/pro/me");
        if (pro.in_trial) {
          setState({ status: "trialing", proSummary: pro, attempts });
          return;
        }
        if (pro.subscription_status === "active") {
          setState({ status: "active", proSummary: pro, attempts });
          return;
        }
      } catch { /* fall through to legacy check */ }

      // Legacy one-shot path.
      try {
        const { data: tx } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (tx.payment_status === "paid" || tx.applied) {
          setState({ status: "paid", proSummary: null, attempts });
          return;
        }
        if (tx.status === "expired") {
          setState({ status: "expired", proSummary: null, attempts });
          return;
        }
      } catch { /* keep polling */ }

      // After the 2nd poll without success, actively reconcile against
      // Stripe — covers the case where the webhook isn't configured in the
      // user's Stripe dashboard or hasn't delivered yet. /pro/checkout/sync
      // fetches the Checkout Session directly and applies the subscription
      // server-side. Idempotent.
      if (attempts >= 2 && !syncAttempted) {
        syncAttempted = true;
        try {
          const { data: sync } = await api.post("/pro/checkout/sync", { session_id: sessionId });
          if (sync.synced && sync.pro) {
            if (sync.pro.in_trial) {
              setState({ status: "trialing", proSummary: sync.pro, attempts });
              return;
            }
            if (sync.pro.subscription_status === "active") {
              setState({ status: "active", proSummary: sync.pro, attempts });
              return;
            }
            if (sync.pro.is_pro) {
              setState({ status: "paid", proSummary: sync.pro, attempts });
              return;
            }
          }
        } catch (e) { /* keep polling */ }
      }

      if (attempts < MAX) {
        setTimeout(tick, 2000);
      } else {
        setState((s) => ({ ...s, status: s.status === "pending" ? "processing_slow" : s.status, attempts }));
      }
    };
    tick();
  }, [sessionId]);

  const { status, proSummary } = state;
  const ok = status === "trialing" || status === "active" || status === "paid";
  const expired = status === "expired";

  // Headline / subline copy per terminal state.
  const headline = (() => {
    if (status === "trialing") {
      return lang === "da" ? "DIN GRATIS PRØVE ER STARTET." : "YOUR FREE TRIAL HAS STARTED.";
    }
    if (status === "active" || status === "paid") {
      return lang === "da" ? "VELKOMMEN TIL PRO CREW." : "WELCOME TO PRO CREW.";
    }
    if (expired) {
      return lang === "da" ? "BETALING UDLØB." : "PAYMENT EXPIRED.";
    }
    if (status === "processing_slow") {
      return lang === "da" ? "BETALINGEN BEHANDLES STADIG." : "PAYMENT STILL PROCESSING.";
    }
    return lang === "da" ? "BEHANDLER BETALING…" : "PROCESSING PAYMENT…";
  })();

  const subline = (() => {
    if (status === "trialing") {
      const days = proSummary?.trial_days_left ?? 14;
      const endIso = proSummary?.trial_end_date;
      const endStr = endIso
        ? new Date(endIso).toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { day: "2-digit", month: "long", year: "numeric" })
        : "";
      return lang === "da"
        ? `Du har ${days} dage gratis Pro. Vi opkræver først dit kort ${endStr ? `den ${endStr}` : "når prøveperioden slutter"}. Annullér når som helst.`
        : `You've got ${days} days of free Pro. We won't charge your card until ${endStr ? endStr : "the trial ends"}. Cancel anytime.`;
    }
    if (status === "active" || status === "paid") {
      return lang === "da"
        ? "Dit Pro-abonnement er aktivt. Du kan nu hoste større grupper, blive boostet på Udforsk og se din analytics."
        : "Your Pro subscription is active. You can now host larger crews, get boosted on Explore, and view your analytics.";
    }
    if (expired) {
      return lang === "da" ? "Betalings-sessionen udløb. Prøv igen fra Pro-siden." : "The payment session expired. Try again from the Pro page.";
    }
    if (status === "processing_slow") {
      return lang === "da"
        ? "Stripe har bekræftet din betaling, men vi venter stadig på den endelige bekræftelse. Genindlæs siden om et øjeblik — eller gå til din profil for at se status."
        : "Stripe has confirmed your payment, but we're still waiting for final confirmation. Refresh in a moment — or head to your profile to check status.";
    }
    return lang === "da" ? "Vi bekræfter din betaling med Stripe. Et øjeblik…" : "We're confirming your payment with Stripe. One moment…";
  })();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div
          className="inline-flex items-center justify-center w-20 h-20 mx-auto"
          style={{
            background: ok ? "#34C7591F" : (expired ? "#FF3B301F" : "#1A1A1A"),
            border: `1px solid ${ok ? "#34C759" : (expired ? "#FF3B30" : "#262626")}`,
          }}
        >
          {status === "trialing"
            ? <Clock weight="bold" size={36} color="#FFCC00" />
            : ok
              ? <Check weight="bold" size={36} color="#34C759" />
              : expired
                ? <X weight="bold" size={36} color="#FF3B30" />
                : <Crown weight="duotone" size={36} color="#FFCC00" />}
        </div>

        <h1 className="font-display text-5xl tracking-tight mt-6" data-testid="pro-success-headline">
          {headline}
        </h1>
        <p className="text-zinc-400 mt-3" data-testid="pro-success-subline">{subline}</p>

        {/* Trial perks reminder */}
        {status === "trialing" && (
          <div className="mt-8 border border-[#FFCC00]/40 bg-[#FFCC00]/[0.06] p-5 text-left" data-testid="pro-success-trial-perks">
            <div className="label-overline" style={{ color: "#FFCC00" }}>
              {lang === "da" ? "Hvad er lige låst op" : "What just unlocked"}
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <Check weight="bold" size={14} className="mt-0.5 text-[#34C759] shrink-0" />
                <span>{lang === "da" ? "Op til 50 deltagere pr. session" : "Up to 50 participants per session"}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check weight="bold" size={14} className="mt-0.5 text-[#34C759] shrink-0" />
                <span>{lang === "da" ? "Boostet placering på Udforsk" : "Boosted placement on Explore"}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check weight="bold" size={14} className="mt-0.5 text-[#34C759] shrink-0" />
                <span>{lang === "da" ? "Privat analytics-dashboard" : "Private analytics dashboard"}</span>
              </li>
            </ul>
            <p className="text-[11px] text-zinc-500 mt-4">
              {lang === "da"
                ? "Du kan administrere eller annullere abonnementet fra din profil — uden binding."
                : "You can manage or cancel the subscription from your profile — no commitment."}
            </p>
          </div>
        )}

        {/* CTAs */}
        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          {ok ? (
            <>
              <button
                data-testid="pro-success-dashboard"
                onClick={() => nav("/dashboard")}
                className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-5 py-3 font-bold uppercase tracking-widest text-sm"
              >
                {lang === "da" ? "Til Dashboard" : "Go to Dashboard"}
              </button>
              <button
                data-testid="pro-success-analytics"
                onClick={() => nav("/analytics")}
                className="kinetic-btn border border-[#262626] hover:border-white/40 px-5 py-3 font-bold uppercase tracking-widest text-sm"
              >
                {lang === "da" ? "Se Analytics" : "View Analytics"}
              </button>
              <Link
                to="/profile"
                data-testid="pro-success-profile"
                className="kinetic-btn border border-[#262626] hover:border-white/40 px-5 py-3 font-bold uppercase tracking-widest text-sm flex items-center gap-2"
              >
                <CreditCard weight="bold" size={14} />
                {lang === "da" ? "Administrér abonnement" : "Manage subscription"}
              </Link>
            </>
          ) : status === "processing_slow" ? (
            <>
              <button
                data-testid="pro-success-force-sync"
                onClick={async () => {
                  setState((s) => ({ ...s, status: "pending" }));
                  try {
                    const { data: sync } = await api.post("/pro/checkout/sync", { session_id: sessionId });
                    if (sync.synced && sync.pro?.is_pro) {
                      setState({
                        status: sync.pro.in_trial ? "trialing" : (sync.pro.subscription_status === "active" ? "active" : "paid"),
                        proSummary: sync.pro,
                        attempts: state.attempts,
                      });
                      return;
                    }
                  } catch { /* ignore */ }
                  setState((s) => ({ ...s, status: "processing_slow" }));
                }}
                className="kinetic-btn bg-[#FFCC00] hover:bg-[#e6b800] text-black px-5 py-3 font-bold uppercase tracking-widest text-sm"
              >
                {lang === "da" ? "Aktivér nu" : "Activate now"}
              </button>
              <button
                data-testid="pro-success-refresh"
                onClick={() => window.location.reload()}
                className="kinetic-btn border border-[#262626] hover:border-white/40 px-5 py-3 font-bold uppercase tracking-widest text-sm"
              >
                {lang === "da" ? "Genindlæs" : "Refresh"}
              </button>
              <button
                data-testid="pro-success-profile-fallback"
                onClick={() => nav("/profile")}
                className="kinetic-btn border border-[#262626] hover:border-white/40 px-5 py-3 font-bold uppercase tracking-widest text-sm"
              >
                {lang === "da" ? "Til profil" : "Go to profile"}
              </button>
            </>
          ) : (
            <button
              data-testid="pro-success-back"
              onClick={() => nav("/pro")}
              className="kinetic-btn border border-[#262626] hover:border-white/40 px-5 py-3 font-bold uppercase tracking-widest text-sm"
            >
              {lang === "da" ? "Tilbage til Pro" : "Back to Pro"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
